/**
 * Graph Polygons from Coordinates generator (6.MGSR.3.2).
 *
 * Axis-aligned rectangles are placed with integer vertices; the student finds a
 * horizontal or vertical side length, the perimeter, or the area, all derived
 * from the coordinates via exact horizontal/vertical distance. A CoordinateModel
 * with the polygon's four segments is attached so the drawn figure matches the
 * graded answer.
 *
 * Families:
 *   F1: length of a horizontal side (number)
 *   F2: length of a vertical side (number)
 *   F3: perimeter of the rectangle (number)
 *   F4: area of the rectangle (number)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { coordinateDistance } from "../engine/geometry";
import type { CoordinateModel, CoordinatePoint } from "../graph/plot-model";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "graphing-polygons" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3" | "F4";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], max: 6 },
  standard: { families: ["F1", "F2", "F3"], max: 8 },
  hard: { families: ["F1", "F2", "F3", "F4"], max: 10 },
  challenge: { families: ["F3", "F4"], max: 12 },
};

function seg(from: CoordinatePoint, to: CoordinatePoint) {
  return { from, to };
}

export class GraphingPolygonsGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      // Axis-aligned rectangle: x from x1 to x2, y from y1 to y2, with a
      // positive width and height.
      const x1 = rng.int(-cfg.max, cfg.max - 2);
      const width = rng.int(2, cfg.max);
      const x2 = x1 + width;
      const y1 = rng.int(-cfg.max, cfg.max - 2);
      const height = rng.int(2, cfg.max);
      const y2 = y1 + height;

      const bl: CoordinatePoint = { x: rat(x1), y: rat(y1), closed: true };
      const br: CoordinatePoint = { x: rat(x2), y: rat(y1), closed: true };
      const tr: CoordinatePoint = { x: rat(x2), y: rat(y2), closed: true };
      const tl: CoordinatePoint = { x: rat(x1), y: rat(y2), closed: true };
      const win = cfg.max + 1;
      const graph: CoordinateModel = {
        kind: "coordinate",
        xMin: -win,
        xMax: win,
        yMin: -win,
        yMax: win,
        points: [bl, br, tr, tl],
        segments: [seg(bl, br), seg(br, tr), seg(tr, tl), seg(tl, bl)],
      };

      const w = coordinateDistance(rat(x1), rat(x2));
      const h = coordinateDistance(rat(y1), rat(y2));

      let prompt: string;
      let latex: string;
      let answer: Rational;
      let stepDesc: string;

      const verts = `A(${x1}, ${y1}), B(${x2}, ${y1}), C(${x2}, ${y2}), D(${x1}, ${y2})`;

      if (family === "F1") {
        answer = w;
        prompt = `A rectangle has vertices ${verts}. Find the length of the horizontal side AB.`;
        latex = `AB = |${x2} - (${x1})| = ${answer.toLatex()}`;
        stepDesc = "For a horizontal segment, subtract the x-coordinates and take the absolute value.";
      } else if (family === "F2") {
        answer = h;
        prompt = `A rectangle has vertices ${verts}. Find the length of the vertical side BC.`;
        latex = `BC = |${y2} - (${y1})| = ${answer.toLatex()}`;
        stepDesc = "For a vertical segment, subtract the y-coordinates and take the absolute value.";
      } else if (family === "F3") {
        answer = w.add(h).mul(rat(2));
        prompt = `A rectangle has vertices ${verts}. Find its perimeter.`;
        latex = `P = 2(${w.toLatex()} + ${h.toLatex()}) = ${answer.toLatex()}`;
        stepDesc = "Find the width and height from the coordinates, then use P = 2(w + h).";
      } else {
        answer = w.mul(h);
        prompt = `A rectangle has vertices ${verts}. Find its area.`;
        latex = `A = ${w.toLatex()} \\times ${h.toLatex()} = ${answer.toLatex()}`;
        stepDesc = "Find the width and height from the coordinates, then multiply them.";
      }

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: prompt },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [{ description: stepDesc, latex }],
        hints: [
          { level: 1, text: "Side lengths come from the distance between vertices along an axis." },
          {
            level: 2,
            text: "Horizontal length is the difference of x-values; vertical length is the difference of y-values.",
          },
        ],
        subskills: topic.subskills,
        graph,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const graphingPolygons = new GraphingPolygonsGenerator();
